<template>
  <v-dialog
    :value="value"
    max-width="900px"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card class="rounded-lg">
      <v-card-title class="title primary--text text--darken-3 px-4 py-2">
        {{ title }}
        <v-spacer></v-spacer>
        <v-chip label color="primary" class="white--text title mr-2">
          {{ selectedIds.length }}
        </v-chip>
        <span class="black--text text-body-1">
          of {{ variables.length }} selected
        </span>
        <template v-if="showVisits">
          <v-chip label color="primary" class="white--text title ml-4 mr-2">
            {{ lastIds.length + deltaIds.length }}
          </v-chip>
          <span class="black--text text-body-1">
            last visits &amp; changes
          </span>
        </template>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text style="padding: 0px 16px 16px 16px;">
        <v-text-field
          v-model="search"
          label="Show variables with a domain or name that matches this text:"
          prepend-inner-icon="mdi-magnify"
          clearable
          class="ma-0 pa-0 pt-4"
          hide-details
        ></v-text-field>

        <v-data-table
          :items="variables"
          :headers="shownHeaders"
          :search="search"
          :custom-filter="customFilter"
          item-key="uid"
          dense
          disable-pagination
          hide-default-footer
          class="mt-3"
          @current-items="setShownItems"
        >
          <template v-slot:header.selected>
            <v-tooltip top color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <span class="d-flex align-center" v-on="{ ...tooltip }">
                  <v-simple-checkbox
                    :value="allShownSelected"
                    :indeterminate="someShownSelected"
                    hide-details
                    dense
                    @input="toggleAllShown"
                  />
                  <span v-if="showVisits">First Visit</span>
                </span>
              </template>
              <span>Select or clear all {{ shownItems.length }} shown</span>
            </v-tooltip>
          </template>

          <template v-slot:header.last>
            <span class="d-flex align-center">
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <v-simple-checkbox
                      :value="allShownHave('last')"
                      :indeterminate="someShownHave('last')"
                      :disabled="shownWithVisits.length === 0"
                      hide-details
                      dense
                      @input="toggleAllShownMeasure('last')"
                    />
                  </span>
                </template>
                <span>
                  Select or clear all {{ shownWithVisits.length }} shown with
                  saved visits
                </span>
              </v-tooltip>
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">Last Visit</span>
                </template>
                <span>
                  The variable at its last visit, as a variable of its own.
                  Offered where the variable's first and last visits differ.
                </span>
              </v-tooltip>
            </span>
          </template>

          <template v-slot:header.delta>
            <span class="d-flex align-center">
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    <v-simple-checkbox
                      :value="allShownHave('delta')"
                      :indeterminate="someShownHave('delta')"
                      :disabled="shownWithVisits.length === 0"
                      hide-details
                      dense
                      @input="toggleAllShownMeasure('delta')"
                    />
                  </span>
                </template>
                <span>
                  Select or clear all {{ shownWithVisits.length }} shown with
                  saved visits
                </span>
              </v-tooltip>
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">Change</span>
                </template>
                <span>
                  Change from first to last visit, as a variable of its own.
                  Offered where the variable's first and last visits differ.
                </span>
              </v-tooltip>
            </span>
          </template>

          <template v-slot:item="props">
            <tr>
              <td :style="{ width: widths.selected }">
                <v-simple-checkbox
                  :value="isSelected(props.item)"
                  hide-details
                  dense
                  @input="toggle(props.item)"
                />
              </td>
              <template v-if="showVisits">
                <td
                  v-for="m in visitMeasures"
                  :key="m.value"
                  :style="{ width: widths[m.value] }"
                >
                  <v-simple-checkbox
                    v-if="hasVisits(props.item)"
                    :value="m.ids.includes(props.item.uid)"
                    hide-details
                    dense
                    @input="toggleMeasure(m, props.item)"
                  />
                </td>
              </template>
              <td class="text-subtitle-1" :style="{ width: widths.category }">
                <v-row align="center" class="ma-0 pa-0 flex-nowrap">
                  <img
                    :src="'/images/' + props.item.category + '-icon-128.png'"
                    :title="props.item.category"
                    style="height: 2em;"
                    class="ma-1"
                    @error="missingDomainFallback"
                  />
                  <span class="ml-1">{{ props.item.category }}</span>
                </v-row>
              </td>
              <td
                class="text-subtitle-1"
                :style="{ width: widths.abbreviation }"
              >
                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      {{
                        useLongScaleNames
                          ? props.item.label
                          : props.item.abbreviation
                      }}
                    </span>
                  </template>
                  <span>
                    {{
                      useLongScaleNames
                        ? props.item.description
                        : props.item.label
                    }}
                  </span>
                </v-tooltip>
              </td>
              <td
                class="text-subtitle-1"
                :style="{ width: widths.data_category }"
              >
                {{ props.item.data_category }}
              </td>
              <td
                class="text-subtitle-1 text-wrap"
                :style="{ width: widths.description, lineHeight: '1.25em' }"
              >
                {{ props.item.description }}
              </td>
            </tr>
          </template>

          <template v-slot:no-data>
            No variables available.
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn text color="primary" @click="clearAll">
          <v-icon left>mdi-close-circle-outline</v-icon>Clear all
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('input', false)">
          <v-icon left dark>close</v-icon>Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/datasetSummary/types';
import { missingDomainFallback } from '@/utils/helpers';

// column widths, without and with the Last Visit and Change columns
const PLAIN_WIDTHS = {
  selected: '5%',
  category: '22%',
  abbreviation: '28%',
  data_category: '12%',
  description: '33%',
};
const VISIT_WIDTHS = {
  selected: '11%',
  last: '9%',
  delta: '8%',
  category: '18%',
  abbreviation: '22%',
  data_category: '9%',
  description: '23%',
};

export default {
  name: 'VariablePickerDialog',
  props: {
    // dialog open state (v-model)
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    // variables offered here, each already carrying a `uid` (see variableKey)
    variables: {
      type: Array,
      required: true,
    },
    // selected uids; updated via the .sync modifier
    selectedIds: {
      type: Array,
      required: true,
    },
    // uids also wanted at their last visit, and as their first-to-last
    // change; updated via the .sync modifier. Leave both null to hide the
    // Last Visit and Change columns altogether.
    lastIds: {
      type: Array,
      default: null,
    },
    deltaIds: {
      type: Array,
      default: null,
    },
    // uids of the variables with a saved first and last visit
    visitUids: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      shownItems: [],
      missingDomainFallback: missingDomainFallback,
      headers: [
        {
          text: '',
          value: 'selected',
          sortable: false,
          filterable: false,
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Domain',
          value: 'category',
          sortable: true,
          filterable: true,
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Variable',
          value: 'abbreviation',
          sortable: true,
          filterable: true,
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Type',
          value: 'data_category',
          sortable: true,
          filterable: true,
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Description',
          value: 'description',
          sortable: false,
          filterable: true,
          class: 'text-subtitle-1 font-weight-bold',
        },
      ],
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
    showVisits() {
      return (
        this.lastIds !== null &&
        this.deltaIds !== null &&
        this.visitUids.length > 0
      );
    },
    // in the order the columns appear, as in the Study Group selector's
    // First Visit | Last Visit | Change columns
    visitMeasures() {
      return [
        { value: 'last', ids: this.lastIds || [], event: 'update:lastIds' },
        { value: 'delta', ids: this.deltaIds || [], event: 'update:deltaIds' },
      ];
    },
    widths() {
      return this.showVisits ? VISIT_WIDTHS : PLAIN_WIDTHS;
    },
    shownHeaders() {
      const header = (value, extra = {}) => ({
        text: '',
        value,
        sortable: false,
        filterable: false,
        width: this.widths[value],
        class: 'text-subtitle-1 font-weight-bold',
        ...extra,
      });
      const [selected, ...rest] = this.headers.map(h => ({
        ...h,
        width: this.widths[h.value],
      }));
      if (!this.showVisits) return [selected, ...rest];
      return [selected, header('last'), header('delta'), ...rest];
    },
    selectedSet() {
      return new Set(this.selectedIds);
    },
    visitSet() {
      return new Set(this.visitUids);
    },
    shownSelectedCount() {
      return this.shownItems.filter(v => this.selectedSet.has(v.uid)).length;
    },
    // only rows with saved visits offer Last Visit and Change
    shownWithVisits() {
      return this.shownItems.filter(this.hasVisits);
    },
    allShownSelected() {
      return (
        this.shownItems.length > 0 &&
        this.shownSelectedCount === this.shownItems.length
      );
    },
    someShownSelected() {
      return (
        this.shownSelectedCount > 0 &&
        this.shownSelectedCount < this.shownItems.length
      );
    },
  },
  methods: {
    setShownItems(items) {
      this.shownItems = items;
    },
    isSelected(v) {
      return this.selectedSet.has(v.uid);
    },
    emitSelection(ids) {
      this.$emit('update:selectedIds', ids);
    },
    toggle(v) {
      if (this.selectedSet.has(v.uid)) {
        this.emitSelection(this.selectedIds.filter(id => id !== v.uid));
      } else {
        this.emitSelection([...this.selectedIds, v.uid]);
      }
    },
    // acts on the filtered rows only, leaving hidden selections untouched
    toggleAllShown() {
      const shown = this.shownItems.map(v => v.uid);
      if (this.allShownSelected) {
        const drop = new Set(shown);
        this.emitSelection(this.selectedIds.filter(id => !drop.has(id)));
      } else {
        this.emitSelection(
          Array.from(new Set([...this.selectedIds, ...shown]))
        );
      }
    },
    hasVisits(v) {
      return this.visitSet.has(v.uid);
    },
    measure(value) {
      return this.visitMeasures.find(m => m.value === value);
    },
    shownCountWith(value) {
      const ids = new Set(this.measure(value).ids);
      return this.shownWithVisits.filter(v => ids.has(v.uid)).length;
    },
    allShownHave(value) {
      const n = this.shownWithVisits.length;
      return n > 0 && this.shownCountWith(value) === n;
    },
    someShownHave(value) {
      const count = this.shownCountWith(value);
      return count > 0 && count < this.shownWithVisits.length;
    },
    // as toggleAllShown, over the shown rows with saved visits
    toggleAllShownMeasure(value) {
      const m = this.measure(value);
      const shown = this.shownWithVisits.map(v => v.uid);
      if (this.allShownHave(value)) {
        const drop = new Set(shown);
        this.$emit(m.event, m.ids.filter(id => !drop.has(id)));
      } else {
        this.$emit(m.event, Array.from(new Set([...m.ids, ...shown])));
      }
    },
    toggleMeasure(m, v) {
      this.$emit(
        m.event,
        m.ids.includes(v.uid)
          ? m.ids.filter(id => id !== v.uid)
          : [...m.ids, v.uid]
      );
    },
    clearAll() {
      this.emitSelection([]);
      if (!this.showVisits) return;
      this.visitMeasures.forEach(m => this.$emit(m.event, []));
    },
    // matches the filter used by the Variables tab
    customFilter(value, search, item) {
      const lcSearch = search == null ? null : search.toLowerCase();
      return (
        lcSearch == null ||
        item.category.toLowerCase().indexOf(lcSearch) !== -1 ||
        item.abbreviation.toLowerCase().indexOf(lcSearch) !== -1 ||
        item.label.toLowerCase().indexOf(lcSearch) !== -1
      );
    },
  },
};
</script>

<style scoped></style>
